/**
 * A casca de Relatórios (11.10): as abas, os filtros comuns e a declaração de quais cada aba aceita.
 *
 * **Filtros comuns** (FR-55, EXPERIENCE.md): período · vendedor · origem · produto · segmento
 * (Setor) · região (UF) · faixa · etapa · status. **Cada aba declara quais aceita**, e a casca
 * **desabilita** os demais — filtro que a aba não usa não some em silêncio. A declaração espelha o
 * contrato da rota: o parâmetro que a aba não aceita não existe no `openapi.json` dela, e por isso
 * nunca viaja. **Produto** (11.11) é aceito só por Análises: a descrição do item do Orçamento
 * espelhado do ERP (sem catálogo). Status não é aceito por aba nenhuma: fica visível e desabilitado.
 *
 * **A seleção persiste por tela na sessão** (`sessionStorage`), e o período escolhido numa aba vale
 * para as demais (FR-54). Sem período escolhido, cada aba abre no padrão do serviço — o Placar nos
 * últimos 7 dias (FR-58), o Resultado do mês no mês corrente, a Saúde nos últimos 30.
 */

import type { AbaComDados, AtalhoDePeriodo, Recorte } from './api'
import { parametrosDoRecorte } from './api'

export type Filtro =
  | 'periodo'
  | 'vendedor'
  | 'origem'
  | 'produto'
  | 'segmento'
  | 'regiao'
  | 'faixa'
  | 'etapa'
  | 'status'

/** A ordem e os rótulos da barra de filtros (texto que o usuário lê, com acento). */
export const ROTULOS_DOS_FILTROS = {
  periodo: 'Período',
  vendedor: 'Vendedor',
  origem: 'Origem',
  produto: 'Produto',
  segmento: 'Segmento',
  regiao: 'Região',
  faixa: 'Faixa',
  etapa: 'Etapa',
  status: 'Status',
} as const satisfies Record<Filtro, string>

export const ORDEM_DOS_FILTROS = Object.keys(ROTULOS_DOS_FILTROS) as readonly Filtro[]

export interface DeclaracaoDaAba {
  readonly aba: AbaComDados
  readonly rotulo: string
  /** O que vai na URL (`?aba=`). */
  readonly chave: string
  readonly filtros: ReadonlySet<Filtro>
}

/** As oito abas, na ordem da lista da Gabi (11.10 e 11.11). */
export const ABAS: readonly DeclaracaoDaAba[] = [
  {
    aba: 'resultado',
    rotulo: 'Resultado do mês',
    chave: 'resultado',
    filtros: new Set<Filtro>(['periodo', 'vendedor']),
  },
  { aba: 'placar', rotulo: 'Placar', chave: 'placar', filtros: new Set<Filtro>(['periodo']) },
  {
    aba: 'previsao',
    rotulo: 'Previsão',
    chave: 'previsao',
    filtros: new Set<Filtro>(['vendedor', 'origem', 'segmento', 'regiao', 'faixa', 'etapa']),
  },
  {
    aba: 'saude',
    rotulo: 'Saúde do funil',
    chave: 'saude',
    filtros: new Set<Filtro>(['periodo', 'vendedor', 'origem', 'segmento', 'regiao', 'faixa']),
  },
  {
    aba: 'prospeccao',
    rotulo: 'Prospecção',
    chave: 'prospeccao',
    filtros: new Set<Filtro>(['periodo', 'vendedor', 'origem', 'segmento', 'regiao', 'faixa']),
  },
  {
    aba: 'execucao',
    rotulo: 'Execução',
    chave: 'execucao',
    filtros: new Set<Filtro>([
      'periodo',
      'vendedor',
      'origem',
      'segmento',
      'regiao',
      'faixa',
      'etapa',
    ]),
  },
  {
    aba: 'carteira',
    rotulo: 'Carteira',
    chave: 'carteira',
    filtros: new Set<Filtro>(['periodo', 'vendedor', 'origem', 'segmento', 'regiao', 'faixa']),
  },
  {
    aba: 'analises',
    rotulo: 'Análises',
    chave: 'analises',
    filtros: new Set<Filtro>([
      'periodo',
      'vendedor',
      'origem',
      'produto',
      'segmento',
      'regiao',
      'faixa',
    ]),
  },
]

export const ABA_PADRAO: AbaComDados = 'resultado'

export function abaDaChave(chave: unknown): AbaComDados | undefined {
  return ABAS.find((item) => item.chave === chave)?.aba
}

export function declaracaoDa(aba: AbaComDados): DeclaracaoDaAba {
  const declaracao = ABAS.find((item) => item.aba === aba)
  if (declaracao === undefined) throw new Error(`Aba sem declaração: ${aba}`)
  return declaracao
}

/** A seleção da casca — os mesmos filtros para todas as abas; cada aba usa os que aceita. */
export interface SelecaoDeFiltros {
  /** `null` = o padrão de cada aba (nada escolhido na sessão). */
  readonly recorte: Recorte | null
  readonly vendedores: readonly string[]
  readonly origens: readonly string[]
  readonly segmentos: readonly string[]
  readonly regioes: readonly string[]
  readonly faixas: readonly string[]
  readonly etapas: readonly string[]
  /** 11.11 — as descrições de item escolhidas (produto, sem catálogo). */
  readonly produtos: readonly string[]
}

export const SELECAO_VAZIA: SelecaoDeFiltros = {
  recorte: null,
  vendedores: [],
  origens: [],
  segmentos: [],
  regioes: [],
  faixas: [],
  etapas: [],
  produtos: [],
}

/** Os filtros de lista e o parâmetro de query de cada um (os nomes do contrato). */
export const PARAMETROS_DE_LISTA = {
  vendedor: ['vendedores', 'vendedor'],
  origem: ['origens', 'origem'],
  produto: ['produtos', 'produto'],
  segmento: ['segmentos', 'segmento'],
  regiao: ['regioes', 'regiao'],
  faixa: ['faixas', 'faixa'],
  etapa: ['etapas', 'etapa'],
} as const satisfies Partial<Record<Filtro, readonly [keyof SelecaoDeFiltros, string]>>

export type FiltroDeLista = keyof typeof PARAMETROS_DE_LISTA

/**
 * A query de uma aba: só os filtros que ela aceita, em ordem estável (a chave de cache depende
 * disso). `filtrosAceitos` é a declaração — o drill-down recebe a da aba de onde veio o número.
 */
export function consultaDaAba(
  selecao: SelecaoDeFiltros,
  filtrosAceitos: ReadonlySet<Filtro>,
  extras: Record<string, string> = {},
): string {
  const parametros = new URLSearchParams(extras)
  if (filtrosAceitos.has('periodo')) {
    for (const [nome, valor] of parametrosDoRecorte(selecao.recorte)) {
      parametros.append(nome, valor)
    }
  }
  for (const filtro of Object.keys(PARAMETROS_DE_LISTA) as FiltroDeLista[]) {
    if (!filtrosAceitos.has(filtro)) continue
    const [campo, nome] = PARAMETROS_DE_LISTA[filtro]
    for (const valor of [...(selecao[campo])].sort()) {
      parametros.append(nome, valor)
    }
  }
  return parametros.toString()
}

/** A chave na sessão é **por pessoa**: dois usuários no mesmo navegador não herdam filtros. */
function chaveNaSessao(usuarioId: string): string {
  return `relatorios:filtros:${usuarioId}`
}

/**
 * Os atalhos do contrato. `Record<AtalhoDePeriodo, true>` é o teste de completude: atalho novo no
 * serviço (tipo gerado) sem linha aqui não compila, e linha que não é atalho também não.
 */
const ATALHOS_DO_CONTRATO: Record<AtalhoDePeriodo, true> = {
  ultimos_7_dias: true,
  ultimos_30_dias: true,
  ultimos_60_dias: true,
  este_mes: true,
  mes_passado: true,
}

function ehAtalho(valor: unknown): valor is AtalhoDePeriodo {
  return typeof valor === 'string' && Object.hasOwn(ATALHOS_DO_CONTRATO, valor)
}

const DATA_PURA = /^\d{4}-\d{2}-\d{2}$/

function ehDataPura(valor: unknown): valor is string {
  return typeof valor === 'string' && DATA_PURA.test(valor) && !Number.isNaN(Date.parse(valor))
}

function lista(valor: unknown): string[] {
  return Array.isArray(valor) ? valor.filter((item): item is string => typeof item === 'string') : []
}

/** O recorte guardado, se for um recorte que o serviço aceita — senão, nenhum (o padrão da aba). */
function recorteValido(valor: unknown): Recorte | null {
  if (typeof valor !== 'object' || valor === null) return null
  const bruto = valor as Record<string, unknown>
  if (bruto.tipo === 'atalho' && ehAtalho(bruto.atalho)) {
    return { tipo: 'atalho', atalho: bruto.atalho }
  }
  if (
    bruto.tipo === 'intervalo' &&
    ehDataPura(bruto.inicio) &&
    ehDataPura(bruto.fim) &&
    bruto.inicio <= bruto.fim
  ) {
    return { tipo: 'intervalo', inicio: bruto.inicio, fim: bruto.fim }
  }
  return null
}

/** A seleção guardada na sessão para **esta pessoa** — tolerante a lixo (entrada do navegador). */
export function lerSelecaoDaSessao(usuarioId: string): SelecaoDeFiltros {
  try {
    const bruto = window.sessionStorage.getItem(chaveNaSessao(usuarioId))
    if (bruto === null) return SELECAO_VAZIA
    const valor = JSON.parse(bruto) as Record<string, unknown>
    return {
      recorte: recorteValido(valor.recorte),
      vendedores: lista(valor.vendedores),
      origens: lista(valor.origens),
      segmentos: lista(valor.segmentos),
      regioes: lista(valor.regioes),
      faixas: lista(valor.faixas),
      etapas: lista(valor.etapas),
      produtos: lista(valor.produtos),
    }
  } catch {
    return SELECAO_VAZIA
  }
}

export function guardarSelecaoNaSessao(usuarioId: string, selecao: SelecaoDeFiltros): void {
  try {
    window.sessionStorage.setItem(chaveNaSessao(usuarioId), JSON.stringify(selecao))
  } catch {
    /* Sessão cheia ou bloqueada: a tela continua funcionando, só não lembra. */
  }
}

/** As 27 UFs — Região = UF (FR-55). */
export const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS', 'MT', 'PA', 'PB', 'PE',
  'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC', 'SE', 'SP', 'TO',
] as const

/** Ouro, Prata, Bronze (FR-81) — o valor do contrato e o rótulo. */
export const FAIXAS = [
  { valor: 'ouro', rotulo: 'Ouro' },
  { valor: 'prata', rotulo: 'Prata' },
  { valor: 'bronze', rotulo: 'Bronze' },
] as const
